// src/hooks/useGroupWebSocket.ts

import { useEffect, useRef, useCallback } from 'react';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { WsMessageType } from '../types/groupChat';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost'
  : '';

// 네이티브 WebSocket URL (ws:// or wss://)
function getNativeWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname === 'localhost'
    ? 'localhost'
    : window.location.host;
  return `${proto}//${host}/ws-native`;
}

interface UseGroupWebSocketOptions {
  roomIds: number[];
  token: string | null;
  onMessage: (msg: WsMessageType) => void;
  enabled: boolean;
}

export function useGroupWebSocket({
  roomIds,
  token,
  onMessage,
  enabled,
}: UseGroupWebSocketOptions) {
  const clientRef = useRef<Client | null>(null);
  const roomIdsRef = useRef<number[]>(roomIds);
  const onMessageRef = useRef(onMessage);

  // 최신 콜백 유지
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    roomIdsRef.current = roomIds;
  }, [roomIds]);

  useEffect(() => {
    if (!enabled || !token || roomIds.length === 0) return;

    const subscribeAll = (client: Client) => {
      const currentRoomIds = roomIdsRef.current;
      currentRoomIds.forEach((roomId) => {
        client.subscribe(`/topic/room/${roomId}`, (msg: IMessage) => {
          try {
            const parsed: WsMessageType = JSON.parse(msg.body);
            if (parsed.type === 'AI_COMPLETE' || parsed.type === 'AI_CHUNK') {
              console.log(`[GroupWS] ${parsed.type} 수신 (roomId=${roomId}):`, parsed);
            }
            onMessageRef.current(parsed);
          } catch (e) {
            console.error('[GroupWS] 메시지 파싱 오류:', e, msg.body);
          }
        });
        client.subscribe(`/topic/room/${roomId}/typing`, (msg: IMessage) => {
          try {
            const parsed: WsMessageType = JSON.parse(msg.body);
            onMessageRef.current(parsed);
          } catch (e) {
            console.error('[GroupWS] 타이핑 파싱 오류:', e);
          }
        });
        client.subscribe(`/topic/room/${roomId}/read`, (msg: IMessage) => {
          try {
            const parsed: WsMessageType = JSON.parse(msg.body);
            onMessageRef.current(parsed);
          } catch (e) {
            console.error('[GroupWS] 읽음 파싱 오류:', e);
          }
        });
      });

      client.subscribe('/user/queue/notification', (msg: IMessage) => {
        try {
          const parsed: WsMessageType = JSON.parse(msg.body);
          onMessageRef.current(parsed);
        } catch (e) {
          console.error('[GroupWS] 알림 파싱 오류:', e);
        }
      });

      client.subscribe('/topic/online', (msg: IMessage) => {
        try {
          const parsed: WsMessageType = JSON.parse(msg.body);
          onMessageRef.current(parsed);
        } catch (e) {
          console.error('[GroupWS] 온라인 파싱 오류:', e);
        }
      });
    };

    let cancelled = false;

    // 네이티브 WebSocket 우선 시도, 실패 시 SockJS 폴백
    const connectNative = (): Client => {
      console.log('[GroupWS] 네이티브 WebSocket 연결 시도...');
      const client = new Client({
        brokerURL: getNativeWsUrl(),
        connectHeaders: { Authorization: `Bearer ${token}` },
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          console.log('[GroupWS] 네이티브 WebSocket 연결됨');
          subscribeAll(client);
        },
        onDisconnect: () => {
          console.log('[GroupWS] 네이티브 WebSocket 연결 해제');
        },
        onStompError: (frame) => {
          console.error('[GroupWS] 네이티브 STOMP 오류:', frame);
        },
        onWebSocketError: () => {
          // 네이티브 실패 → SockJS 폴백
          if (cancelled) return;
          console.warn('[GroupWS] 네이티브 WebSocket 실패, SockJS 폴백 시도...');
          client.deactivate();
          if (!cancelled) {
            const sockjsClient = connectSockJS();
            sockjsClient.activate();
            clientRef.current = sockjsClient;
          }
        },
      });
      return client;
    };

    const connectSockJS = (): Client => {
      console.log('[GroupWS] SockJS 연결 시도...');
      const client = new Client({
        webSocketFactory: () => new SockJS(`${API_BASE}/ws`) as WebSocket,
        connectHeaders: { Authorization: `Bearer ${token}` },
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          console.log('[GroupWS] SockJS 연결됨');
          subscribeAll(client);
        },
        onDisconnect: () => {
          console.log('[GroupWS] SockJS 연결 해제');
        },
        onStompError: (frame) => {
          console.error('[GroupWS] SockJS STOMP 오류:', frame);
        },
      });
      return client;
    };

    const client = connectNative();
    client.activate();
    clientRef.current = client;

    return () => {
      cancelled = true;
      clientRef.current?.deactivate();
      clientRef.current = null;
    };
    // roomIds 변경 시 재연결
  }, [enabled, token, roomIds.join(',')]);

  // 메시지 발행
  const publish = useCallback((destination: string, body: object) => {
    const client = clientRef.current;
    if (client && client.connected) {
      client.publish({
        destination,
        body: JSON.stringify(body),
      });
    }
  }, []);

  // 채팅 메시지 전송
  const sendMessage = useCallback(
    (roomId: number, content: string, mentionAi: boolean) => {
      publish(`/app/room/${roomId}/send`, { content, mentionAi });
    },
    [publish]
  );

  // 타이핑 이벤트 전송
  const sendTyping = useCallback(
    (roomId: number, userName: string) => {
      publish(`/app/room/${roomId}/typing`, { userName });
    },
    [publish]
  );

  // 읽음 표시 전송
  const sendRead = useCallback(
    (roomId: number, lastReadMsgId: number) => {
      publish(`/app/room/${roomId}/read`, { lastReadMsgId });
    },
    [publish]
  );

  const isConnected = () => clientRef.current?.connected ?? false;

  return { sendMessage, sendTyping, sendRead, isConnected };
}
